import { RecommendHit } from "@algolia/recommend";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useLocale } from "next-intl";
import { useMemo } from "react";

import Grid from "~/components/common/grid";
import SkeletonProductTile from "~/components/common/skeletons/skeleton-product-tile";
import { useConfiguration } from "~/contexts/configuration";
import { useServerSideProps } from "~/contexts/server-side-props";
import { useCartWithPendingStatus } from "~/hooks/use-cart";
import {
  cartRecommenderElegibleProduct,
  mergeHistWithProductsInformation,
  recommenderRequest,
} from "~/services/algolia-utils";
import Editorials from "~/types/editorials";
import appRoutes from "~/utils/app-routes";
import evaUtils from "~/utils/eva-utils";

import ProductCarouselSwiper from "./product-carousel-swiper";
import styles from "./product-carousel-swiper.module.scss";
type Props = {
  productReferenceFields: Editorials.ProductsAlgoliaRecommender;
  enableLoop: boolean;
  listScope: string;
};

export default function ProductCarouselAlgolia({ productReferenceFields, enableLoop, listScope }: Props) {
  const locale = useLocale();
  const router = useRouter();
  const { serverSideProps } = useServerSideProps();
  const { cart } = useCartWithPendingStatus();
  const { eva } = useConfiguration();

  const isCartPage = useMemo(() => {
    return router.pathname === appRoutes.CART;
  }, [router]);

  const productID = useMemo(() => {
    if (!isCartPage) {
      return (router.query.slug as string)?.split("-").at(-1) ?? "";
    } else {
      if (productReferenceFields?.type === "trending-items") {
        return "";
      } else {
        const latestCarProduct = cartRecommenderElegibleProduct(cart);
        return latestCarProduct ? latestCarProduct : "";
      }
    }
  }, [cart, isCartPage, productReferenceFields, router]);

  const carouselDataQuery = useMemo(() => {
    return ["algolia-product-carousel", !!productID ? productID : "trending-items"];
  }, [productID]);

  const { data, isLoading } = useQuery({
    queryKey: carouselDataQuery,
    staleTime: Infinity,
    enabled: typeof productID !== "undefined",
    queryFn: async () => {
      const response = await recommenderRequest({
        productReferenceFields,
        locale,
        productID,
        organizationUnitId: serverSideProps.organizationUnitId,
      });
      const productIDS = (response as RecommendHit[]).map((item) => item.objectID);
      //Removed hook
      const productsInformation = await evaUtils.getProductsByIdsWithChildren(eva, productIDS);
      const mergedData = mergeHistWithProductsInformation(response as RecommendHit[], productsInformation);
      return mergedData;
    },
  });
  return (
    <>
      {isLoading ? (
        // Here should be placed a loader
        <Grid className={styles.preloadGrid}>
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonProductTile key={`preload-${i}`} />
          ))}
        </Grid>
      ) : (
        <ProductCarouselSwiper isAlgolia products={data} listScope={listScope} enableLoop={enableLoop} />
      )}
    </>
  );
}
